import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Container,
  VStack,
  Spinner,
  Text,
  Alert,
  Button,
  SimpleGrid,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { useQueryClient } from '@tanstack/react-query'
import { useListOverview, useMaterializeList } from '../hooks/useContactList'
import { useJobPolling } from '../hooks/useJobPolling'
import { useIsUnionTier } from '../hooks/useIsUnionTier'
import { OverviewHeader } from '../components/listOverview/OverviewHeader'
import { UniverseSummaryPanel } from '../components/listOverview/UniverseSummaryPanel'
import { DemographicsPanel } from '../components/listOverview/DemographicsPanel'
import { GeographyPanel } from '../components/listOverview/GeographyPanel'
import { UnionPanel } from '../components/listOverview/UnionPanel'
import { NotesPanel } from '../components/listOverview/NotesPanel'

export function ListOverviewPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: overview, isLoading, error } = useListOverview(id)
  const materialize = useMaterializeList()
  const isUnionTier = useIsUnionTier()

  // Materialize enqueues a background job (WI-42) rather than running inline (slice 6b). This
  // page previously fired materialize.mutate(id) fire-and-forget with zero completion feedback
  // and zero invalidation of its own overview cache -- coherent-but-silent before, and would have
  // stayed silent (never actively misleading) if left alone. Converting it anyway to close the
  // loop for real: a toast on completion, and the overview/list caches actually refresh instead
  // of waiting out the 5-minute staleTime.
  const [refreshJobId, setRefreshJobId] = useState<string | null>(null)
  const { data: refreshJob, isTerminal: refreshTerminal } = useJobPolling(refreshJobId)
  const isRefreshing = materialize.isPending || (!!refreshJobId && !refreshTerminal)

  const handleRefresh = async () => {
    if (!id) return
    try {
      const { jobId } = await materialize.mutateAsync(id)
      setRefreshJobId(jobId)
    } catch {
      toaster.create({ title: 'Refresh failed', type: 'error', duration: 5000 })
    }
  }

  // Drives the materialize job to completion (WI-42). Branches on `!refreshTerminal` alone --
  // NOT `!refreshTerminal || !refreshJob` -- because a 404 (job not owned / not found) reports
  // isTerminal=true with job undefined; guarding on `!refreshJob` too would swallow that case and
  // leave the button spinning forever. Invalidation happens here, on completion, never on the
  // enqueue mutation's onSuccess -- materialize genuinely persists (rewrites list membership,
  // stamps IsMaterialized/memberCount), so invalidating before the job finishes would just
  // refetch stale pre-job data.
  useEffect(() => {
    if (!refreshTerminal) return

    if (refreshJob?.status === 'Succeeded') {
      queryClient.invalidateQueries({ queryKey: ['lists'] })
      if (id) {
        queryClient.invalidateQueries({ queryKey: ['lists', id] })
        queryClient.invalidateQueries({ queryKey: ['lists', id, 'overview'] })
      }
      toaster.create({ title: 'List refreshed', type: 'success', duration: 4000 })
    } else {
      toaster.create({
        title: 'Refresh failed',
        description: refreshJob?.errorMessage ?? 'An error occurred.',
        type: 'error',
        duration: 5000,
      })
    }
    setRefreshJobId(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTerminal, refreshJob])

  if (isLoading) {
    return <VStack py={12}><Spinner size="lg" color="accent" /><Text color="gray.500">Loading overview…</Text></VStack>
  }
  if (!!error || !overview) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert.Root status="error" borderRadius="md"><Alert.Indicator />Could not load this list.</Alert.Root>
        <Button mt={4} onClick={() => navigate('/dashboard/lists')}>Back to lists</Button>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack gap={6} align="stretch">
        <OverviewHeader
          overview={overview}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
          onViewMap={() => navigate('/dashboard/analytics-map', { state: { listId: overview.id } })}
          onTagUniverse={() => navigate('/dashboard/analytics-map', { state: { listId: overview.id, action: 'tag' } })}
          onUseInCampaign={() => navigate('/dashboard/campaigns', { state: { listId: overview.id, action: 'create' } })}
        />
        <UniverseSummaryPanel overview={overview} />
        <DemographicsPanel overview={overview} />
        <SimpleGrid columns={isUnionTier ? { base: 1, md: 2 } : { base: 1 }} gap={6}>
          <GeographyPanel overview={overview} />
          <UnionPanel overview={overview} />
        </SimpleGrid>
        <NotesPanel listId={overview.id} notes={overview.notes} />
      </VStack>
    </Container>
  );
}
